import {
  api_affiliate_settings_path,
  api_edit_privacy_settings_path,
  api_email_settings_path,
  api_gift_subscription_settings_path,
  api_update_affiliate_settings_path,
  api_update_email_settings_path,
  api_update_privacy_settings_path,
  api_update_user_info_path,
  api_user_account_path,
} from '../../routes.js';
import { getJWT } from '../../utils/jwtHelper';
import request from './request';

export const USER_ACCOUNT_SETTINGS_KEY = 'user_dashboard_settings_key';
export const GIFT_SUBSCRIPTION_SETTINGS_KEY = 'gift_subscription_settings_key';
export const USER_EMAIL_SETTINGS_KEY = 'user_email_settings_key';
export const AFFILIATE_SETTINGS_KEY = 'affiliate_settings_key';
export const PRIVACY_SETTINGS_KEY = 'privacy_settings_key';

export interface SettingsResponse {
  canResumeSubscription: boolean;
  canRestartSubscription: boolean;
  canUpdatePayment: boolean;
  canViewPaymentHistory: boolean;
  eligibleToUpgrade: boolean;
  isRevcatSubscriber: boolean;
  trial: {
    accessDaysRemaining?: number;
    dateToCharge?: string;
    expiredFromTrial?: boolean;
    paidThroughDate?: string;
  };
}

export interface UpdateUserParams {
  user: {
    name: string;
    email: string;
    password?: string;
  };
}

export interface UpdateUserResponse {
  emailVerificationRequired?: boolean;
  message?: string;
  error?: string;
}

export interface UpdateAffiliateResponse {
  message?: string;
  error?: string;
}

export interface GiftSubscriptionReponse {
  offer: {
    initialPrice: number;
  };
}

export interface EmailSettings {
  articleNotifications: string;
  commentNotificationEnabled: boolean;
  sendEventEmails: boolean;
  sendForumDigest: boolean;
  sendPromotionEmails: boolean;
  sendProductEmails: boolean;
  surveyEnabled: boolean;
  weeklyBookmarkDigestEnabled: boolean;
}

export interface EmailSettingsParams {
  user: {
    article_notifications?: string;
    comment_notification_enabled?: boolean;
    send_event_emails?: boolean;
    send_forum_digest?: boolean;
    send_promotion_emails?: boolean;
    send_product_emails?: boolean;
    survey_enabled?: boolean;
    weekly_bookmark_digest_enabled?: boolean;
  };
}

export interface AffiliateSettingsParams {
  payment_id: string;
  payment_type: string;
}

export interface AffiliateSettings {
  paymentId?: string;
  paymentType: {
    label?: string;
    value?: string;
  };
}

export interface UpdateAffiliateSettingsResponse {
  message?: string;
}

export interface UpdateEmailSettingResponse {
  message?: string;
}

export interface PrivacySettingsParams {
  user: {
    ccpa_do_not_sell_personal_info: boolean;
  };
}

export interface PrivacSettingResponse {
  ccpaDoNotSellPersonalInfo: boolean;
}

export interface PrivacySettingsResponse {
  success?: boolean;
  message?: string;
}

export interface UpdatePrivacySettingsResponse {
  success?: boolean;
}

export async function getUserSetting(): Promise<SettingsResponse> {
  const resp = await request('get', api_user_account_path());
  return resp;
}

export async function updateUserInfo(params: UpdateUserParams): Promise<UpdateUserResponse> {
  const resp = await request('put', api_update_user_info_path(params));
  return resp;
}

export async function giftSubscriptionSettings(): Promise<GiftSubscriptionReponse> {
  const resp = await request('get', api_gift_subscription_settings_path());
  return resp;
}

export async function getEmailSettings(): Promise<EmailSettings> {
  const resp = await request('get', api_email_settings_path(getJWT()));
  return resp;
}

export async function updateEmailSettings(
  params: EmailSettingsParams
): Promise<UpdateEmailSettingResponse> {
  const resp = await request('put', api_update_email_settings_path({ ...params, ...getJWT() }));
  return resp;
}

export async function getAffiliateSettings(): Promise<AffiliateSettings> {
  const resp = await request('get', api_affiliate_settings_path());
  return resp;
}

export async function updateAffiliateSettings(
  params: AffiliateSettingsParams
): Promise<UpdateAffiliateSettingsResponse> {
  const resp = await request(
    'put',
    api_update_affiliate_settings_path({ affiliate_settings: params })
  );
  return resp;
}

export async function getPrivacySettings(): Promise<PrivacSettingResponse> {
  const resp = await request('get', api_edit_privacy_settings_path());
  return resp;
}

export async function updatePrivacySettings(
  params: PrivacySettingsParams
): Promise<UpdatePrivacySettingsResponse> {
  const resp = await request('put', api_update_privacy_settings_path(params));
  return resp;
}
